<template>
  <div
    class="quest-chapter"
    :class="{
      'quest-chapter--full-screen': fullScreen
    }">
    <TabsMenu
      class="mb-48"
      :items="{
        description: 'Description',
        sql_editor: 'SQL Editor',
        db_info: 'Database Info',
        sql_reference: 'SQL Reference',
      }"
      :activeItem="activeTab"
      :showKeyboardShortcuts="true"
      @activeTab="handleTabChange"/>

    <Tab :active="activeTab === 'description'">
      <div class="quest-chapter__description-tab max-width-720">
        <div
          class="quest-chapter__description"
          v-html="formattedDescription" />

        <Hints
          @hint-opened="handleHintOpened"
          :chapterId="chapterItem.id"
          :hintCount="chapterItem.hints_count || 0"
          :usedHints="chapterItem.used_hints || []"
          :usedHintIndices="chapterItem.used_hint_indices || []" />

        <div class="quest-chapter__current-query mb-32">
          <h3>Your query</h3>

          <div v-if="query">
            <pre>
              <code ref="user_query" class="language-pgsql hljs" v-html="query" />
            </pre>
          </div>

          <div
            v-if="!query"
            class="quest-chapter__current-query-tip"
            @click="handleTipLinkClick"
            v-html="$options.filters.md(`:point_up: Prepare a query in the [SQL Editor](#sql_editor) and submit it from here when you're ready.`)" />
        </div>

        <div
          class="quest-chapter__actions">

          <button
            class="quest-chapter__submit btn btn--fat"
            :class="{
              'btn--disabled': !queryValid || formLock
            }"
            :disabled="!queryValid"
            @click.prevent="handleSubmit">

            <span v-if="formLock">
              SUBMITTING YOUR QUERY...
            </span>

            <span v-else>
              SUBMIT CURRENT QUERY
            </span>
          </button>
        </div>
      </div>
    </Tab>

    <Tab :active="activeTab === 'sql_editor'">
      <div class="quest-chapter__workspace max-width-960">
        <SQLEditor
          ref="sqlEditor"
          :chapterItem="chapterItem"
          :formLock="formLock"
          v-on:query-changed="queryChanged"
          v-on:switch-tab="handleSwitchTabFromEditor"
          :initialQuery="initialQuery"
          :solutionQuery="solutionQuery" />
      </div>
    </Tab>

    <Tab :active="activeTab === 'db_info'">
      <DatabaseDocumentation
        :showDatasetMenu="false"
        :forceDatasetId="datasetId" />
    </Tab>

    <Tab :active="activeTab === 'sql_reference'">
      <SqlReference />
    </Tab>

    <transition
      enter-active-class="slide-in-down"
      leave-active-class="slide-in-up">

      <ConfirmationPopup
        v-if="confirmationPopupShown"
        :copy="confirmationPopupCopy"
        @submit="handleConfirmationPopup" />
    </transition>
  </div>
</template>

<script>
import axios from "axios";
import SQLEditor from "components/quest/sql-editor";
import ConfirmationPopup from "components/quest/confirmation-popup";
import Hints from "components/quest/hints";
import TabsMenu from "components/tabs-menu";
import Tab from "components/tab";
import DatabaseDocumentation from "components/database-documentation";
import SqlReference from "components/sql-reference";
import { loadMathjax } from "controllers/mathjax";
import { highlightAll, forceHighlightNode } from "modules/code-highlight";
import { notify } from "modules/notify";
import { confettiBoom } from "modules/confetti-cannon";
import sharedStore from "components/quest/shared-store";

export default {
  name: "QuestChapter",
  components: {
    SQLEditor,
    ConfirmationPopup,
    Hints,
    TabsMenu,
    Tab,
    DatabaseDocumentation,
    SqlReference
  },
  data() {
    return {
      sharedStore,
      activeTab: "description",
      query: null,
      formLock: false,
      confirmationPopupShown: false,
      confirmationPopupCopy: null,
      confirmationPopupAction: null,
      queryValid: false,
      showChapterIntro: false,
      showChapterOutro: false,
      tabOrder: ["description", "sql_editor", "db_info", "sql_reference"],
      initialQuery: sharedStore.state.userQueriesById[this.chapterItem.id]
    }
  },
  props: {
    chapterItem: {
      type: Object
    },
    questId: {
      type: String
    },
    fullScreen: {
      type: Boolean,
      default: false
    },
    isLastChapter: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    name() {
      return this.chapterItem.name;
    },
    formattedDescription() {
      const username = this.sharedStore.state.username;
      const description = this.chapterItem.description || "";

      return description.replaceAll("%username%", username);
    },
    datasetId() {
      return this.chapterItem.dataset_id;
    },
    solutionQuery() {
      return this.sharedStore.state.userSolutionQueriesById[this.chapterItem.id] || null;
    },
    hasOutro() {
      return this.chapterItem.has_outro ||
        (this.chapterItem.outro && this.chapterItem.outro.trim());
    }
  },
  watch: {
    chapterItem(newVal, oldVal) {
      this.query = null;
      this.queryValid = false;
      this.showChapterOutro = false;
      this.activeTab = "description";

      // Check if chapter has intro text
      if (newVal.intro && newVal.intro.trim().length > 0) {
        this.showChapterIntro = true;
      } else {
        this.showChapterIntro = false;
      }

      this.$refs.sqlEditor?.clearQuery();

      window.scrollTo(0, 0);
    },
    query(newVal) {
      if (newVal && newVal.toString().length > 10) {
        this.queryValid = true;
      } else {
        this.queryValid = false;
      }
    }
  },
  methods: {
    handleKeyboardShortcut(event) {
      // Check for CMD+Enter (Mac) or CTRL+Enter (Windows)
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        // Only submit if description tab is active, query is valid, and neither intro nor outro is shown
        if (this.activeTab === 'description' && this.queryValid && !this.formLock) {
          event.preventDefault();
          event.stopPropagation();

          this.handleSubmit();
        }
      }

      // Check for CMD+Shift+< or CTRL+Shift+<
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && (event.key === '<' || event.code === 'Comma')) {
        event.preventDefault();
        this.switchToPreviousTab();
      }

      // Check for CMD+Shift+> or CTRL+Shift+>
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && (event.key === '>' || event.code === 'Period')) {
        event.preventDefault();
        this.switchToNextTab();
      }
    },
    switchToPreviousTab() {
      const currentIndex = this.tabOrder.indexOf(this.activeTab);
      const previousIndex = currentIndex > 0 ? currentIndex - 1 : this.tabOrder.length - 1;
      this.activeTab = this.tabOrder[previousIndex];

      if(this.activeTab === "description" && this.$refs.user_query) {
        this.$nextTick(() => {
          forceHighlightNode(this.$refs.user_query);
        });
      }

      if(this.activeTab === "sql_editor" && this.$refs.sqlEditor) {
        this.$nextTick(() => {
          this.$refs.sqlEditor.focus();
        });
      }
    },
    switchToNextTab() {
      const currentIndex = this.tabOrder.indexOf(this.activeTab);
      const nextIndex = currentIndex < this.tabOrder.length - 1 ? currentIndex + 1 : 0;
      this.activeTab = this.tabOrder[nextIndex];

      if(this.activeTab === "description" && this.$refs.user_query) {
        this.$nextTick(() => {
          forceHighlightNode(this.$refs.user_query);
        });
      }

      if(this.activeTab === "sql_editor" && this.$refs.sqlEditor) {
        this.$nextTick(() => {
          this.$refs.sqlEditor.focus();
        });
      }
    },
    handleTabChange(newTab) {
      this.activeTab = newTab;

      if(newTab === "description" && this.$refs.user_query) {
        forceHighlightNode(this.$refs.user_query);
      }

      if(newTab === "sql_editor" && this.$refs.sqlEditor) {
        this.$nextTick(() => {
          this.$refs.sqlEditor.focus();
        });
      }
    },
    queryChanged(query) {
      this.query = query;
      this.sharedStore.updateUserQuery(this.chapterItem.id, query);
    },
    handleSwitchTabFromEditor(direction) {
      if (direction === "previous") {
        this.switchToPreviousTab();
      } else if (direction === "next") {
        this.switchToNextTab();
      }
    },
    handleSubmit() {
      this.confirmationPopupCopy = "Are you sure you want to submit your query and finish the chapter?";
      this.confirmationPopupAction = { type: "submit" };
      this.confirmationPopupShown = true;
    },
    handleConfirmationPopup(confirm) {
      const action = this.confirmationPopupAction;

      this.confirmationPopupShown = false;
      this.confirmationPopupAction = null;

      if(!confirm || !action) {
        return;
      }

      if(action.type === "submit") {
        this.submitSolution();
      }
    },
    submitSolution() {
      if(this.formLock) {
        return;
      }

      this.confirmationPopupShown = false;

      this.formLock = true;

      let apiPath = `/api/quests/chapters/${this.chapterItem.id}/evaluate.json`;

      let formData = new FormData();

      formData.append("chapter_participant_id", this.questId);

      formData.append("chapter[query]", this.query);

      axios.patch(apiPath, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
        }
      }).then(response => {
        if(response.data.solved) {
          const unlockedChapters = response.data.unlocked_chapters || [];
          this.sharedStore.mergeChapters(unlockedChapters);

          confettiBoom({
            maxCount: 500
          });

          if (response.data.quest_paused) {
            this.sharedStore.setQuestPaused(true);
            this.sharedStore.setPauseRequested(false);
            this.sharedStore.updateTimer({
              duration: response.data.current_duration_sec,
              startedAt: null
            });
          } else {
            this.$nextTick(() => {
              this.sharedStore.updateTimer({
                duration: response.data.current_duration_sec,
                startedAt: response.data.current_chapter_started_at
              });
            });

            if (response.data.quest_finished) {
              this.sharedStore.setQuestFinished();
            }

            this.sharedStore.nextScreen();
          }
        } else {
          notify({
            message: "Your query doesn't produce the correct result. Please try again!",
            type: "error"
          });
        }
      }).catch(error => {
        console.error(error);
        notify({
          message: "An error occurred while submitting your query. Please try again!",
          type: "error"
        });
      }).finally(() => {
        this.formLock = false;
      });
    },
    handleHintOpened() {
      this.$nextTick(() => {
        highlightAll();
      });
    },
    handleTipLinkClick(event) {
      const target = event.target;

      if (target.tagName === 'A' && target.getAttribute('href') === '#sql_editor') {
        event.preventDefault();
        this.handleTabChange('sql_editor');
      }
    }
  },
  mounted() {
    // Add keyboard shortcut listener
    window.addEventListener('keydown', this.handleKeyboardShortcut);

    this.$nextTick(() => {
      highlightAll();
      loadMathjax();

      if(this.$refs.user_query) {
        forceHighlightNode(this.$refs.user_query);
      }
    });
  },
  beforeDestroy() {
    // Remove keyboard shortcut listener
    window.removeEventListener('keydown', this.handleKeyboardShortcut);
  }
}
</script>

<style lang="scss">
@keyframes slideInDown {
  from {
    transform: translate3d(-50%, -100%, 0);
    visibility: visible;
  }

  to {
    transform: translate3d(-50%, 0, 0);
  }
}

@keyframes slideInUp {
  from {
    transform: translate3d(-50%, 0, 0);
    visibility: visible;
  }

  to {
    transform: translate3d(-50%, -100%, 0);
  }
}

.slide-in-up {
  animation: slideInUp 0.2s ease-in-out both;
}

.slide-in-down {
  animation: slideInDown 0.2s ease-in-out both;
}

.quest-chapter {
  $root: &;

  overflow: hidden;

  &__actions {
    display: flex;
    align-items: center;
  }

  &__submit {
    margin-right: $px24;
  }

  &__description {
    margin-bottom: $px24;

    p:first-child {
      margin-top: 0;
    }

    .table {
      overflow-x: auto;
    }

    table {
      border: $px1 solid $grey-7;
      border-radius: $px4;
      border-collapse: collapse;

      thead {
        tr:first-child {
          background-color: $grey-7;

          font-weight: 700;
        }
      }

      td {
        white-space: nowrap;
      }

      tr {
        &:nth-child(even) {
          background-color: $grey-10;
        }
      }

      th,
      td {
        padding: $px4 $px8;

        text-align: left;
      }
    }
  }

  &__left-column {
    width: 45%;
    padding-right: $px32;
  }

  &__current-query {
    pre {
      white-space: pre-line;
    }

    code {
      white-space: pre;
    }
  }

  &__current-query-tip {
    a {
      border-bottom: $px1 solid $grey-7;
      border-bottom-style: dashed;

      text-decoration: none;
    }
  }

  &__sql-editor {
    width: 55%;
  }
}
</style>
